import { FC, useContext, useState, useEffect, useMemo } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { i18n as I18n } from 'utils/i18n';
import { getVenueImage } from 'utils/venueImages';

// constants
import { spaceDisabledForService } from 'utils/booking';
import { sortFields } from 'constants/sortFields';
import { RESULTS_PER_PAGE } from 'constants/fieldData';
import { RADIX } from 'constants/radix';
import { TABLET_WIDTH } from 'constants/breakpoints';

// context
import { SearchContext } from 'state/search';

import useClientServices from 'hooks/useClientServices';
import useStoredBooking from 'hooks/useStoredBooking';

import useSpaceSearch, {
  SpaceSearchData,
  emptyData,
} from 'hooks/useSpaceSearch';

// components
import SearchResultFilters from 'components/searchResultFilters';
import Venue from 'components/venue';
import SpaceList from 'components/spaceList';
import SearchMap from 'components/searchMap';
import Pager from 'components/pager';
import SpaceFilters from 'components/spaceFilters';
import MobileSpaceFilters from 'components/mobileSpaceFilters';
import LoadingOverlay from 'components/shared/loadingOverlay';

import useHeader from './hooks/useHeader';

interface Props {
  selectedVenueId?: number;
  selectedSpaceIds: number[];
  selectSpace: (space: any, venue: any) => any;
  initialData?: SpaceSearchData;
}

const SearchResults: FC<Props> = ({
  selectedVenueId,
  selectedSpaceIds = [],
  selectSpace = () => {},
  initialData = emptyData,
}) => {
  const router = useRouter();

  const { filters, actions: searchActions } = useContext(SearchContext);
  const location = filters.latLng
    ? I18n.t('shared.current_location')
    : filters.location;

  const [{ service: serviceId = '' } = {}] = useStoredBooking();
  const { data: services } = useClientServices();

  const header = useHeader(filters);

  const service = useMemo(
    () =>
      serviceId
        ? services.find(({ id }) => id === Number(serviceId))
        : undefined,
    [services, serviceId]
  );
  const [currentView, setCurrentView] = useState('list');
  const [validSortOptions, updateValidSortOptions] = useState(
    Object.keys(sortFields)
  );
  const [currentPage, updateCurrentPage] = useState(1);
  const [mapVenue, setMapSelectedProperty] = useState();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [displayStickyNav, toggleStickyPanel] = useState(false);
  const [width, setWidth] = useState(0);

  const {
    data: {
      venues: results = [],
      alternativeVenues = [],
      totalVenues,
      totalSpaces,
      aggregates,
    } = initialData,
    loading: loadingResults,
  } = useSpaceSearch({ filters, viewType: currentView, page: currentPage });
  const { actions } = useContext(SearchContext);

  useEffect(() => {
    actions.updatePreviousSearches(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions.updatePreviousSearches, filters]);

  useEffect(() => {
    updateCurrentPage(1);
  }, [filters.location, filters.roomTypes]);

  const searchResults = results.length > 0 ? results : alternativeVenues;

  const handleResize = () => {
    if (window) {
      setWidth(window.innerWidth);
    }
  };

  useEffect(() => {
    handleResize();

    if (window) {
      window.addEventListener('resize', handleResize);
    }
  }, []);

  useEffect(() => {
    const resetSearch = url => {
      if (url.startsWith('/properties/') || url.startsWith('/rooms/')) {
        return;
      }

      if (url !== '/sign-in' && router.pathname !== '/sign-in')
        searchActions.resetSearch();
    };

    router.events.on('routeChangeStart', resetSearch);
    return () => router.events.off('routeChangeStart', resetSearch);
  }, [router, searchActions]);

  useEffect(() => {
    if (loadingResults && currentView === 'list') {
      setMapSelectedProperty(undefined);
    }
  }, [loadingResults, setMapSelectedProperty, currentView]);

  if (location === '' && validSortOptions.includes('Distance')) {
    const availableSortOptions = validSortOptions.filter(
      field => field !== 'Distance'
    );

    updateValidSortOptions(availableSortOptions);
  }

  const handlePageChange = event => {
    event.preventDefault();

    const nextPage = parseInt(currentPage, RADIX) + 1;

    updateCurrentPage(nextPage);
  };

  const handleChangeSortBy = sortBy => {
    updateCurrentPage(1);
    searchActions.onSetSort(sortBy);
  };

  const handleChangeView = switchOn => {
    updateCurrentPage(1);
    setCurrentView(switchOn ? 'list' : 'map');
  };

  const handleSelectVenueInMap = selectedVenue => {
    if (currentView === 'list') {
      handleChangeView(false);
    }

    setMapSelectedProperty(selectedVenue);
  };

  const noResultsMessage = () => {
    if (!location) {
      return I18n.t('search_results.alternative_venues.no_matches');
    }

    if (location && alternativeVenues.length > 0) {
      return (
        <>
          {I18n.t('search_results.alternative_venues.no_location_results')}
          <strong>{location}</strong>.{' '}
          {I18n.t('search_results.alternative_venues.nearest_properties')}
        </>
      );
    }

    return (
      <>
        {I18n.t('search_results.alternative_venues.no_location_results')}
        <strong>{location}</strong>.{' '}
        {I18n.t('search_results.alternative_venues.check_spelling')}
      </>
    );
  };

  return (
    <>
      <Head>
        <title>{header.title}</title>
        <meta
          name="description"
          content={header.description}
          key="description"
        />
        <meta name="title" content={header.title} key="title" />
      </Head>
      <div className="venue-search" id="venue-search">
        {width < TABLET_WIDTH && results.length > 0 && (
          <MobileSpaceFilters
            changeViewType={handleChangeView}
            currentViewType={currentView}
            onChange={searchActions.onChangeFilter}
            updateCurrentPage={updateCurrentPage}
            aggregates={aggregates}
          />
        )}
        <SearchResultFilters
          location={location}
          roomsTotal={results.length > 0 ? totalSpaces : 0}
          propertiesTotal={results.length > 0 ? totalVenues : 0}
          currentSortBy={filters.sort}
          currentViewType={currentView}
          changeViewType={handleChangeView}
          changeSortBy={handleChangeSortBy}
          sortOptions={validSortOptions}
          loading={loadingResults}
        />
        {!loadingResults && currentView === 'list' && results.length < 1 && (
          <>
            <div className="venue-search__alternative_venues_message">
              <p>{noResultsMessage()}</p>
            </div>
          </>
        )}
        {width > TABLET_WIDTH && (
          <SpaceFilters
            currentViewType={currentView}
            onChange={searchActions.onChangeFilter}
            updateCurrentPage={updateCurrentPage}
            aggregates={aggregates}
          />
        )}

        <LoadingOverlay active={loadingResults && currentView === 'list'}>
          {currentView === 'list' && (
            <>
              {searchResults.length > 0 && (
                <ul className="venue-search__list">
                  {searchResults.map(venue => (
                    <Venue
                      key={`${venue.id}-${(venue.spaces || [])
                        .map(({ id }) => id)
                        .join('-')}`}
                      id={venue.id}
                      name={venue.name}
                      url={`${I18n.t('navigation.venues.url')}/${venue.id}`}
                      image={getVenueImage(venue)}
                      latLng={venue.latLng}
                      location={location}
                      distance={venue.distance}
                      showSpacesDefault
                      onClickMap={() => handleSelectVenueInMap(venue)}
                      facets={venue.facets}
                      floorplans={venue.floorplans}
                      discount={venue.discount}
                      discounts={venue.discounts}
                      venueSpaces={venue.spaces}
                      organisationName={venue.organisation.name}
                      spaces={() => (
                        <SpaceList
                          key={(venue.spaces || [])
                            .map(({ id }) => id)
                            .join('-')}
                          venue={venue}
                          spaces={venue.spaces}
                          selectedVenueId={selectedVenueId}
                          selectedSpaceIds={selectedSpaceIds}
                          onAddToBooking={selectSpace}
                          spaceServiceClash={spaceDisabledForService(service)}
                          discounts={venue.discounts}
                          fetchSpaces={false}
                        />
                      )}
                    />
                  ))}
                </ul>
              )}

              {results.length > 0 && (
                <Pager
                  className="venue-search--pager"
                  loading={loadingResults}
                  currentPage={currentPage}
                  perPage={RESULTS_PER_PAGE}
                  total={totalVenues}
                  onClick={handlePageChange}
                />
              )}
            </>
          )}
          {currentView === 'map' && (
            <div className="search-map">
              <SearchMap
                location={location}
                venues={results.length ? results : alternativeVenues}
                loading={loadingResults}
                selectedVenue={mapVenue}
                onSelectVenue={handleSelectVenueInMap}
                geolocation={filters.latLng}
              />
              {mapVenue && (
                <SpaceList
                  spaces={mapVenue.spaces || []}
                  venue={mapVenue}
                  selectedVenueId={selectedVenueId}
                  selectedSpaceIds={selectedSpaceIds}
                  onAddToBooking={selectSpace}
                  spaceServiceClash={spaceDisabledForService(service)}
                />
              )}
              {!mapVenue && (
                <p className="search-map__text">
                  {I18n.t('location_map.select_venue_prompt')}
                </p>
              )}
            </div>
          )}
        </LoadingOverlay>
      </div>
    </>
  );
};

export default SearchResults;
